import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';
import {
  getDedicatedReportGroupBy,
  isDedicatedReportRoute,
} from '@/views/reports/report-route-behavior';
import type { ReportMetricColumn } from '@/views/reports/report-base-build';

export type ReportMetricNameContext = {
  translate: (key: string) => string;
  customEvents: Record<string, { name?: string }>;
  getCustomColumns: () => Record<string, { name?: string }>;
};

export const resolveReportMetricName = (key: string, context: ReportMetricNameContext) => {
  if (key.startsWith(METRICS_CUSTOM_EVENT)) {
    const pureKey = key.replace(METRICS_CUSTOM_EVENT, '');
    return context.customEvents[pureKey]?.name || key;
  }

  if (key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)) {
    const pureKey = key.replace(METRICS_CUSTOM_EVENT_REVENUE, '');
    return `${context.customEvents[pureKey]?.name || key} (${context.translate('revenue')})` || key;
  }

  if (key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
    const pureKey = key.replace(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE, '');
    return `${context.customEvents[pureKey]?.name || key} (${context.translate('Hidden Revenue')})` || key;
  }

  if (key.startsWith(METRICS_CUSTOM_COLUMN)) {
    return context.getCustomColumns()[key.slice(METRICS_CUSTOM_COLUMN.length)]?.name;
  }

  return context.translate(`metric.${key}`);
};

export type DedicatedMetricsBundles = {
  base: { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
  conversions: { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
  postbacks: { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
  errors: { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
  'live-clicks': { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
  'live-visits': { avaliable_metrics: string[]; report_exclude: Record<string, string[]> };
};

export const resolveCustomMetricsBundle = (
  routeName: string,
  bundles: DedicatedMetricsBundles,
) => {
  if (!isDedicatedReportRoute(routeName)) {
    return bundles.base;
  }

  switch (getDedicatedReportGroupBy(routeName)) {
    case 'conversions':
      return bundles.conversions;
    case 'postbacks':
      return bundles.postbacks;
    case 'errors':
      return bundles.errors;
    case 'live-clicks':
      return bundles['live-clicks'];
    case 'live-visits':
      return bundles['live-visits'];
    default:
      return bundles.base;
  }
};

export const buildMetricColumn = (
  value: string,
  reportMetricsName: (key: string) => string,
  options?: Record<string, unknown>,
): ReportMetricColumn => {
  return {
    text: reportMetricsName(value),
    value,
    ...(options ? options : {}),
  };
};

export const buildAdditionalMainColumns = (options: {
  treeLevels: string[] | undefined;
  dimensions: Record<string, { mainColumn: string }>;
  reportMetricsName: (key: string) => string;
}) => {
  return options.treeLevels
    ?.filter((item) => !!item)
    ?.map((item) => buildMetricColumn(
      options.dimensions[item].mainColumn,
      options.reportMetricsName,
      { disabled: true },
    )) || [];
};

export const buildMainMetricsColumns = (options: {
  type: string;
  routeName: string;
  dimensions: Record<string, { mainColumn?: string; additionalColumns?: string[] }>;
  metrics: string[];
  isHideFilters: boolean;
  additionalMainColumns: ReportMetricColumn[];
  customMetricsBundle: { report_exclude: Record<string, string[]> };
  reportMetricsName: (key: string) => string;
}) => {
  const { additionalColumns = [], mainColumn } = options.dimensions[options.type] || {};
  const mainColumnValue = mainColumn
    ? buildMetricColumn(mainColumn, options.reportMetricsName, { disabled: true })
    : null;

  let columns = [...additionalColumns, ...options.metrics].map((value) => {
    return buildMetricColumn(value, options.reportMetricsName);
  });

  if (options.isHideFilters) {
    const exclude = options.customMetricsBundle.report_exclude[options.type];

    if (exclude) {
      columns = columns.filter((item) => !exclude.includes(item.value));
    }
  }

  if (mainColumnValue) {
    return [mainColumnValue, ...options.additionalMainColumns, ...columns];
  }

  return [...options.additionalMainColumns, ...columns];
};

export const resolveMetricsList = (options: {
  routeName: string;
  type: string;
  dedicatedRoute: boolean;
  existingCustomMetrics: ReportMetricColumn[];
  mainMetrics: ReportMetricColumn[];
}) => {
  if (options.dedicatedRoute) {
    return options.existingCustomMetrics;
  }

  return options.mainMetrics;
};

const resolveCustomColumnPureKey = (key: string) => key.slice(METRICS_CUSTOM_COLUMN.length);

export const resolveChartMetricYAxisSuffix = (options: {
  key: string;
  currencySuffix: string;
  getCustomColumnFormat: (pureKey: string) => string | undefined;
  seriesYAxis?: number;
}) => {
  if (options.key.startsWith(METRICS_CUSTOM_EVENT)) {
    return '';
  }

  if (
    options.key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)
    || options.key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)
  ) {
    return options.currencySuffix;
  }

  if (options.key.startsWith(METRICS_CUSTOM_COLUMN)) {
    switch (options.getCustomColumnFormat(resolveCustomColumnPureKey(options.key))) {
      default:
      case 'number':
        return '';
      case 'currency':
        return options.currencySuffix;
      case 'percent':
        return '%';
    }
  }

  switch (options.seriesYAxis) {
    default:
    case 0:
      return '';
    case 1:
      return options.currencySuffix;
    case 2:
      return '%';
  }
};

export const resolveChartMetricYAxis = (options: {
  key: string;
  getCustomColumnFormat: (pureKey: string) => string | undefined;
  seriesYAxis?: number;
}) => {
  if (options.key.startsWith(METRICS_CUSTOM_COLUMN)) {
    switch (options.getCustomColumnFormat(resolveCustomColumnPureKey(options.key))) {
      default:
      case 'number':
        return 0;
      case 'currency':
        return 1;
      case 'percent':
        return 2;
    }
  }

  if (options.key.startsWith(METRICS_CUSTOM_EVENT)) {
    return 0;
  }

  if (
    options.key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)
    || options.key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)
  ) {
    return 1;
  }

  return options.seriesYAxis;
};

export type FormulaMetricOption = {
  text: string;
  value: string;
  archived?: boolean;
};

export const buildFormulaMetricOptions = (options: {
  metrics: string[];
  customEvents: Record<string, { slug: string; name: string; status?: string }>;
  reportMetricsName: (name: string) => string;
}): FormulaMetricOption[] => {
  const archived = Object.values(options.customEvents).reduce((accumulator: FormulaMetricOption[], item) => {
    if (item.status !== 'archived') {
      return accumulator;
    }

    accumulator.push({
      value: `${METRICS_CUSTOM_EVENT}${item.slug}`,
      text: item.name,
      archived: true,
    });
    accumulator.push({
      value: `${METRICS_CUSTOM_EVENT_REVENUE}${item.slug}`,
      text: `${item.name} (revenue)`,
      archived: true,
    });
    accumulator.push({
      value: `${METRICS_CUSTOM_EVENT_HIDDEN_REVENUE}${item.slug}`,
      text: `${item.name} (Hidden Revenue)`,
      archived: true,
    });

    return accumulator;
  }, []);

  return options.metrics
    .filter((name) => !name.startsWith(METRICS_CUSTOM_COLUMN))
    .map((name) => ({
      text: options.reportMetricsName(name),
      value: name,
    }))
    .concat(archived);
};
